/*
  Navegação de abas. Espelha o padrão de side-scroller.js: light DOM, hooks por
  classe, handlers bound no construtor e removidos no disconnectedCallback.

  Markup mínimo — os papéis de ARIA, os ids e a ligação aba<->painel são
  aplicados aqui, para que a section só precise da estrutura:

    <tab-nav>
      <div class="tab-nav__list">
        <button class="tab-nav__tab" type="button">Descrição</button>
        <button class="tab-nav__tab" type="button">Composição</button>
      </div>
      <div class="tab-nav__panel">...</div>
      <div class="tab-nav__panel">...</div>
    </tab-nav>

  Painel e aba se casam por aria-controls quando ele existe; sem ele, vale a
  ordem (n-ésima aba -> n-ésimo painel).
*/

let tabNavCount = 0;

class TabNav extends HTMLElement {
  constructor() {
    super();
    this._tabs = [];
    this._panels = [];
    this._uid = `tab-nav-${++tabNavCount}`;

    this._handleClick = this._handleClick.bind(this);
    this._handleKeydown = this._handleKeydown.bind(this);
  }

  connectedCallback() {
    const list = this.querySelector(".tab-nav__list");
    if (!list) return;

    this._tabs = Array.from(this.querySelectorAll(".tab-nav__tab"));
    this._panels = Array.from(this.querySelectorAll(".tab-nav__panel"));
    if (!this._tabs.length) return;

    list.setAttribute("role", "tablist");
    list.addEventListener("click", this._handleClick);
    list.addEventListener("keydown", this._handleKeydown);

    this._tabs.forEach((tab, i) => {
      const panel = this._panelFor(i);

      if (!tab.id) tab.id = `${this._uid}-tab-${i}`;
      tab.setAttribute("role", "tab");
      if (tab.tagName === "BUTTON" && !tab.hasAttribute("type")) {
        // sem type explícito o botão vira submit e envia o form que o envolver
        tab.type = "button";
      }

      if (panel) {
        if (!panel.id) panel.id = `${this._uid}-panel-${i}`;
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", tab.id);
        tab.setAttribute("aria-controls", panel.id);
      }
    });

    // A aba ativa inicial é a marcada no markup; sem marcação, a primeira.
    // O select() abaixo é quem desliga todas as outras — não dependemos de o
    // Liquid ter escrito aria-selected="false" nas subsequentes.
    const marked = this._tabs.findIndex(
      (tab) =>
        tab.getAttribute("aria-selected") === "true" ||
        tab.hasAttribute("data-active")
    );
    this.select(marked === -1 ? 0 : marked, { focus: false, emit: false });
  }

  disconnectedCallback() {
    const list = this.querySelector(".tab-nav__list");
    if (!list) return;
    list.removeEventListener("click", this._handleClick);
    list.removeEventListener("keydown", this._handleKeydown);
  }

  get selectedIndex() {
    return this._tabs.findIndex(
      (tab) => tab.getAttribute("aria-selected") === "true"
    );
  }

  select(index, { focus = true, emit = true } = {}) {
    if (index < 0 || index >= this._tabs.length) return;

    this._tabs.forEach((tab, i) => {
      const active = i === index;
      const panel = this._panelFor(i);

      tab.setAttribute("aria-selected", active);
      // tabindex móvel: só a aba ativa entra na ordem de tabulação; entre as
      // abas quem navega são as setas (padrão ARIA de tablist).
      tab.tabIndex = active ? 0 : -1;
      tab.classList.toggle("is-active", active);

      if (!panel) return;
      panel.hidden = !active;
      panel.classList.toggle("is-active", active);
    });

    if (focus) {
      const tab = this._tabs[index];
      tab.focus();
      // a lista pode estar dentro de um trilho rolável (side-scroller): traz a
      // aba escolhida para a área visível sem arrastar a página junto.
      tab.scrollIntoView({ block: "nearest", inline: "nearest" });
    }

    if (emit) {
      this.dispatchEvent(
        new CustomEvent("tab-nav:change", {
          bubbles: true,
          detail: {
            index,
            tab: this._tabs[index],
            panel: this._panelFor(index),
          },
        })
      );
    }
  }

  _panelFor(index) {
    const tab = this._tabs[index];
    const id = tab?.getAttribute("aria-controls");
    if (id) return this.querySelector(`#${CSS.escape(id)}`) || null;
    return this._panels[index] || null;
  }

  _handleClick(e) {
    const tab = e.target.closest(".tab-nav__tab");
    if (!tab || !this.contains(tab)) return;
    e.preventDefault();
    this.select(this._tabs.indexOf(tab));
  }

  _handleKeydown(e) {
    const current = this._tabs.indexOf(e.target.closest(".tab-nav__tab"));
    if (current === -1) return;

    const last = this._tabs.length - 1;
    let next;

    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = current === last ? 0 : current + 1;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = current === 0 ? last : current - 1;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = last;
        break;
      default:
        return;
    }

    e.preventDefault();
    this.select(next);
  }
}

// guard como em bundle-progressbar: o arquivo é global, mas nada impede uma
// section de incluí-lo de novo — redefinir o mesmo nome lança e derruba o resto
// do script.
if (!customElements.get("tab-nav")) {
  customElements.define("tab-nav", TabNav);
}
